import axiosInstance from './axiosConfig';

// API base URL for fixing video URLs in development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';
const STORAGE_BASE_URL = API_BASE_URL.replace('/api', '');

/**
 * Fix video URL for local development
 * Converts http://localhost/storage/... to http://localhost:8003/storage/...
 */
const fixVideoUrl = (url: string): string => {
  if (!url) return url;
  // Only fix localhost URLs without port
  if (url.startsWith('http://localhost/storage/')) {
    return url.replace('http://localhost/storage/', `${STORAGE_BASE_URL}/storage/`);
  }
  return url;
};

/**
 * Fix video URLs in GeneratedVideo object
 */
const fixGeneratedVideoUrls = (video: GeneratedVideo): GeneratedVideo => {
  return {
    ...video,
    url: fixVideoUrl(video.url),
  };
};

export type VideoGenerationSource = 'video_ads' | 'image_generation';

export interface GeneratedVideo {
  id: number;
  prompt: string;
  url: string;
  s3_key: string;
  file_size: number;
  aspect_ratio: string;
  duration_seconds: number;
  generation_source?: VideoGenerationSource;
  created_at: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface VideoHistoryResponse {
  success: boolean;
  data: {
    data: GeneratedVideo[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface VideoDetailResponse {
  success: boolean;
  data: GeneratedVideo;
  message?: string;
}

// Async video generation job types
export type VideoJobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export type VideoModelType = 'gemini' | 'seedance' | 'seedance-2-pro' | 'seedance-2-fast' | 'seedance-2-mini' | 'seedance-2-5' | 'minimax-h3' | 'fal-h3-max' | 'fal-h3-max-turbo';

/** 生成できる尺。20秒以上は Seedance 2.5 のみ対応。 */
export type VideoDurationSeconds = 5 | 6 | 8 | 12 | 15 | 20 | 25 | 30;

/** 生成 API が受け付ける参照画像。type は省略できる（バックエンドは nullable）。 */
export interface VideoReferenceImageInput {
  type?: 'brand_logo' | 'product_image' | 'material';
  s3_key?: string;
  image_data?: string;
}

/** ジョブに保存済みの参照画像。`category` は投入時の `type` を写したもの。 */
export interface VideoJobReferenceImage {
  s3_key?: string;
  image_data?: string;
  category?: string;
  type?: string;
}

export interface VideoGenerationJob {
  id: string;
  status: VideoJobStatus;
  prompt: string;
  aspect_ratio: string;
  video_model?: VideoModelType;
  error_message?: string;
  /**
   * 失敗理由の機械判定用コード（バックエンドの App\Support\VideoFailureCode）。
   * `error_message` は技術情報を落とした1文なので、画面の分岐はこちらを見る。
   */
  error_code?: string | null;
  /**
   * ここから下は詳細取得（show）だけが返す。一覧のポーリングでは載らない。
   *
   * 秒数と解像度は DB の値をそのまま返すので、生成時の列挙（VideoDurationSeconds）
   * より広い型で受ける。送信へ回す前に resubmitVideoJob で絞り込む。
   */
  duration_seconds?: number;
  resolution?: string;
  generate_audio?: boolean;
  generation_source?: VideoGenerationSource;
  reference_images?: VideoJobReferenceImage[];
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

export interface AsyncVideoGenerateRequest {
  prompt: string;
  aspect_ratio?: '16:9' | '9:16' | '1:1';
  video_model?: VideoModelType;
  duration_seconds?: VideoDurationSeconds;
  resolution?: '720p' | '1080p';
  reference_images?: VideoReferenceImageInput[];
  generation_source?: VideoGenerationSource;
  generate_audio?: boolean;
  skip_prompt_optimization?: boolean;
}

export interface AsyncVideoGenerateResponse {
  success: boolean;
  job: VideoGenerationJob;
  message?: string;
}

export interface VideoJobsListResponse {
  jobs: VideoGenerationJob[];
}

export interface VideoJobDetailResponse {
  job: VideoGenerationJob;
  video: {
    id: number;
    url: string;
    s3_key: string;
    file_size: number;
    duration_seconds: number;
    created_at: string;
  } | null;
}

export interface VideoJobCancelResponse {
  success: boolean;
  message: string;
}

class VideoGenerationService {
  /**
   * Get user's video generation history
   */
  async getVideoHistory(page: number = 1, source?: VideoGenerationSource): Promise<VideoHistoryResponse> {
    const params = new URLSearchParams({ page: String(page) });
    if (source) params.append('source', source);
    const response = await axiosInstance.get<VideoHistoryResponse>(
      `/videos?${params.toString()}`
    );
    // Fix video URLs for local development
    if (response.data.success && response.data.data?.data) {
      response.data.data.data = response.data.data.data.map(fixGeneratedVideoUrls);
    }
    return response.data;
  }

  /**
   * Get organization's video generation history
   * Returns all videos generated by users in the same organization
   */
  async getOrganizationVideoHistory(page: number = 1, source?: VideoGenerationSource): Promise<VideoHistoryResponse> {
    const params = new URLSearchParams({ page: String(page) });
    if (source) params.append('source', source);
    const response = await axiosInstance.get<VideoHistoryResponse>(
      `/videos/organization?${params.toString()}`
    );
    // Fix video URLs for local development
    if (response.data.success && response.data.data?.data) {
      response.data.data.data = response.data.data.data.map(fixGeneratedVideoUrls);
    }
    return response.data;
  }

  /**
   * Get single video detail
   */
  async getVideoDetail(id: number): Promise<VideoDetailResponse> {
    const response = await axiosInstance.get<VideoDetailResponse>(
      `/videos/${id}`
    );
    // Fix video URL for local development
    if (response.data.success && response.data.data) {
      response.data.data = fixGeneratedVideoUrls(response.data.data);
    }
    return response.data;
  }

  /**
   * Delete generated video
   */
  async deleteVideo(id: number): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.delete<{ success: boolean; message: string }>(
      `/videos/${id}`
    );
    return response.data;
  }

  // ========================================
  // Async Video Generation Methods
  // ========================================

  /**
   * Start async video generation
   */
  async generateAsync(
    prompt: string,
    aspectRatio: '16:9' | '9:16' | '1:1' = '16:9',
    referenceImages?: VideoReferenceImageInput[],
    durationSeconds?: VideoDurationSeconds,
    resolution?: '720p' | '1080p',
    videoModel?: VideoModelType,
    generationSource?: VideoGenerationSource,
    skipPromptOptimization?: boolean,
    generateAudio?: boolean
  ): Promise<AsyncVideoGenerateResponse> {
    const payload: AsyncVideoGenerateRequest = {
      prompt,
      aspect_ratio: aspectRatio,
    };

    if (generationSource) {
      payload.generation_source = generationSource;
    }

    if (videoModel) {
      payload.video_model = videoModel;
    }

    if (durationSeconds) {
      payload.duration_seconds = durationSeconds;
    }

    if (resolution) {
      payload.resolution = resolution;
    }

    if (referenceImages && referenceImages.length > 0) {
      payload.reference_images = referenceImages;
    }

    if (skipPromptOptimization) {
      payload.skip_prompt_optimization = true;
    }

    if (generateAudio !== undefined) {
      payload.generate_audio = generateAudio;
    }

    const response = await axiosInstance.post<AsyncVideoGenerateResponse>(
      '/videos/generate-async',
      payload
    );
    return response.data;
  }

  /**
   * Get list of user's video generation jobs
   */
  async getJobs(status?: VideoJobStatus, source?: VideoGenerationSource): Promise<VideoGenerationJob[]> {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (source) params.source = source;
    const response = await axiosInstance.get<VideoJobsListResponse>('/videos/jobs', { params });
    return response.data.jobs;
  }

  /**
   * Get job detail with generated video
   */
  async getJobDetail(jobId: string): Promise<VideoJobDetailResponse> {
    const response = await axiosInstance.get<VideoJobDetailResponse>(`/videos/jobs/${jobId}`);

    // Fix video URL for local development
    if (response.data.video) {
      response.data.video = {
        ...response.data.video,
        url: fixVideoUrl(response.data.video.url),
      };
    }

    return response.data;
  }

  /**
   * Cancel a pending job
   */
  async cancelJob(jobId: string): Promise<VideoJobCancelResponse> {
    const response = await axiosInstance.delete<VideoJobCancelResponse>(`/videos/jobs/${jobId}`);
    return response.data;
  }

  /**
   * Generate videos in batch
   */
  async generateBatch(
    prompts: string[],
    aspectRatio: '16:9' | '9:16' = '16:9',
    referenceImages?: Array<{
      type: 'brand_logo' | 'product_image' | 'material';
      s3_key?: string;
      image_data?: string;
    }>,
    durationSeconds?: VideoDurationSeconds,
    resolution?: '720p' | '1080p',
    videoModel?: VideoModelType,
    generationSource?: VideoGenerationSource
  ): Promise<{ success: boolean; jobs: VideoGenerationJob[]; queued_count: number; skipped_count: number; message?: string }> {
    const data: any = {
      prompts,
      aspect_ratio: aspectRatio,
    };
    if (referenceImages?.length) data.reference_images = referenceImages;
    if (durationSeconds) data.duration_seconds = durationSeconds;
    if (resolution) data.resolution = resolution;
    if (videoModel) data.video_model = videoModel;
    if (generationSource) data.generation_source = generationSource;

    const response = await axiosInstance.post('/videos/generate-batch', data);
    return response.data;
  }
}

export default new VideoGenerationService();
